import React, { useState, useEffect, useRef, useCallback } from 'react';
import { adminApi } from '../../services/api';
import AgGridTable from '../../components/AgGridTable';
import CallbackConfigurationForm from './CallbackConfigurationForm';
import CallbackEventForm from './CallbackEventForm';
import { copyToClipboard, getCopyIcon } from '../../utils/copyUtils';
import SidebarPanel from '../../components/admin/SidebarPanel';

const CallbackManagementPage = () => {
  const mainGridRef = useRef();
  
  // State management
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [eventsWithConfigs, setEventsWithConfigs] = useState([]);
  const [message, setMessage] = useState(null);
  const [webhookServerMessage, setWebhookServerMessage] = useState(null);
  
  // Side panel state
  const [sidePanel, setSidePanel] = useState({
    isOpen: false,
    type: null, // 'viewEvent', 'editEvent', 'viewConfig', 'editConfig', 'newConfig'
    eventData: null,
    configId: null
  });

  // Webhook servers state
  const [webhookServers, setWebhookServers] = useState([]);
  const [webhookServersLoading, setWebhookServersLoading] = useState(true);
  const [webhookServersError, setWebhookServersError] = useState(null);
  
  // Webhook editing state
  const [editingWebhookId, setEditingWebhookId] = useState(null);
  const [isCreatingWebhook, setIsCreatingWebhook] = useState(false);
  const [webhookFormData, setWebhookFormData] = useState({
    core_server_webhook_url: '',
    core_server_webhook_secret: '',
    description: '',
    is_active: true
  });
  const [webhookSaving, setWebhookSaving] = useState(false);

  // Metrics
  const [metrics, setMetrics] = useState({
    total_callbacks: 0,
    successful_callbacks: 0,
    failed_callbacks: 0,
    success_rate: 0
  });
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricsError, setMetricsError] = useState(null);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState(24);

  // Delete confirmation modal state
  const [deleteModal, setDeleteModal] = useState({
    show: false,
    configId: null,
    configName: null
  });

  // Test results modal state
  const [testResultsModal, setTestResultsModal] = useState({
    show: false,
    configId: null,
    results: [],
    loading: false
  });

  // Copy state to track icon states
  const [copyIconStates, setCopyIconStates] = useState({});

  // Fetch and combine events with their configurations
  const fetchEventsWithConfigurations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch both events and configurations
      const [eventsResponse, configsResponse] = await Promise.all([
        adminApi.get('/admin/callback/system/core/events/manage/'),
        adminApi.get('/admin/callback/system/core/manage/configurations/')
      ]);

      if (!Array.isArray(eventsResponse.data)) {
        throw new Error('Invalid events data received');
      }
      
      if (!Array.isArray(configsResponse.data)) {
        throw new Error('Invalid configurations data received');
      }

      const events = eventsResponse.data;
      const configs = configsResponse.data;

      // Create master rows (events) with configurations as detail data
      // First, group configurations by event_name
      const configsByEventName = configs.reduce((acc, config) => {
        if (!acc[config.event_name]) {
          acc[config.event_name] = [];
        }
        acc[config.event_name].push(config);
        return acc;
      }, {});

      const masterData = events.map(event => {
        const eventConfigs = configsByEventName[event.event_name] || [];
        
        return {
          id: `event_${event.id || event.event_name}`,
          event_name: event.event_name,
          event_description: event.description || '',
          config_count: eventConfigs.length,
          is_active: event.is_active,
          created_at: event.created_at,
          eventData: event,
          configurations: eventConfigs.map(config => ({
            id: config.id,
            config_id: config.id,
            version: config.version,
            priority: config.priority,
            is_active: config.is_active,
            message_template: config.message_template,
            health_system_name: config.health_system_name || 'Global',
            provider_name: config.provider_name || '—',
            department_name: config.department_name || '—',
            clinic_name: config.clinic_name || '—',
            created_at: config.created_at,
            updated_at: config.updated_at,
            configData: config,
            eventData: event
          }))
        };
      });

      // Sort by event name
      const sortedData = masterData.sort((a, b) => a.event_name.localeCompare(b.event_name));

      setEventsWithConfigs(sortedData);
    } catch (err) {
      setError(err.message || 'Failed to fetch events and configurations');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCallbackMetrics = useCallback(async (hours = 24) => {
    try {
      setMetricsLoading(true);
      setMetricsError(null);
      const response = await adminApi.get(`/admin/callback/system/core/metrics/?hours=${hours}`);
      if (response.data && response.data.success) {
        setMetrics(response.data.metrics);
      } else {
        setMetricsError(response.data?.message || 'Failed to fetch callback metrics.');
      }
    } catch (err) {
      console.warn('Metrics API error:', err);
      setMetricsError(err.message || 'An error occurred while fetching callback metrics.');
    } finally {
      setMetricsLoading(false);
    }
  }, []);

  const fetchWebhookServers = useCallback(async () => {
    try {
      setWebhookServersLoading(true);
      setWebhookServersError(null);
      
      // Fetch webhook servers from database
      const [serversResponse, settingsResponse] = await Promise.all([
        adminApi.get('/admin/callback/system/core/manage/servers/'),
        adminApi.get('/admin/callback/system/core/')
      ]);
      let servers = [];   
      // Add database webhook servers
      if (serversResponse.data && Array.isArray(serversResponse.data)) {
        servers = serversResponse.data.map(server => ({
          ...server,
          source: 'database'
        }));
      }
      // Add fallback from settings.py if no database servers exist or if settings.py has data
      if (settingsResponse.data && settingsResponse.data.success) {
        const hasSettingsData = settingsResponse.data.CORE_SERVER_WEBHOOK_URL || settingsResponse.data.CORE_SERVER_WEBHOOK_SECRET_CONFIGURED;
        if (hasSettingsData && servers.length === 0) {
          // Only add settings.py data if no database servers exist
          servers.push({
            id: 'settings-py',
            core_server_webhook_url: settingsResponse.data.CORE_SERVER_WEBHOOK_URL || '',
            core_server_webhook_secret: settingsResponse.data.CORE_SERVER_WEBHOOK_SECRET_CONFIGURED ? '••••••••' : '',
            description: 'From settings.py (Legacy)',
            is_active: true,
            source: 'settings_py',
            created_at: null,
            updated_at: null
          });
        }
      }
      setWebhookServers(servers);
    } catch (err) {
      setWebhookServersError(err.message || 'An error occurred while fetching webhook servers.');
    } finally {
      setWebhookServersLoading(false);
    }
  }, []);
  const handleCreateWebhook = () => {
    setIsCreatingWebhook(true);
    setWebhookFormData({
      core_server_webhook_url: '',
      core_server_webhook_secret: '',
      description: '',
      is_active: true
    });
  };
  const handleEditWebhook = (webhook) => {
    if (webhook.source === 'settings_py') {
      setWebhookServerMessage({ type: 'warning', text: 'Can not edit webhook in the MiraSol environment file. Create a new webhook server instead.' });
      setTimeout(() => setWebhookServerMessage(null), 5000);
      return;
    }
    setEditingWebhookId(webhook.id);
    setWebhookFormData({
      core_server_webhook_url: webhook.core_server_webhook_url || '',
      core_server_webhook_secret: '',
      description: webhook.description || '',
      is_active: webhook.is_active !== false
    });
  };
  const handleCancelWebhookEdit = () => {
    setIsCreatingWebhook(false);
    setEditingWebhookId(null);
    setWebhookFormData({
      core_server_webhook_url: '',
      core_server_webhook_secret: '',
      description: '',
      is_active: true
    });
  };
  const handleSaveWebhook = async () => {
    try {
      setWebhookSaving(true);
      let response;
            if (isCreatingWebhook) {
        response = await adminApi.post('/admin/callback/system/core/manage/servers/', webhookFormData);
      } else {
        response = await adminApi.put(`/admin/callback/system/core/manage/servers/${editingWebhookId}/`, {
          ...webhookFormData,
          id: editingWebhookId
        });
      }      
      if (response.data && (response.data.success !== false)) {
        setWebhookServerMessage({
          type: 'success',
          text: `Webhook server ${isCreatingWebhook ? 'created' : 'updated'} successfully!`
        });
        setTimeout(() => setWebhookServerMessage(null), 5000);
        // Refresh the webhook servers
        await fetchWebhookServers();
        handleCancelWebhookEdit();
      } else {
        setWebhookServerMessage({ type: 'danger', text: response.data?.message || 'Failed to save webhook server.' });
        setTimeout(() => setWebhookServerMessage(null), 5000);
      }
    } catch (err) {
      setWebhookServerMessage({ type: 'danger', text: err.message || 'An error occurred while saving webhook server.' });
      setTimeout(() => setWebhookServerMessage(null), 5000);
    } finally {
      setWebhookSaving(false);
    }
  };
  const handleDeleteWebhook = async (webhookId) => {
    if (webhookId === 'settings-py') {
      setWebhookServerMessage({ type: 'warning', text: 'Cannot delete settings.py webhook.' });
      setTimeout(() => setWebhookServerMessage(null), 5000);
      return;
    }
    if (!window.confirm('Are you sure you want to delete this webhook server?')) {
      return;
    }
    try {
      const response = await adminApi.delete(`/admin/callback/system/core/manage/servers/${webhookId}/`);      
      if (response.data && (response.data.success !== false)) {
        setWebhookServerMessage({ type: 'success', text: 'Webhook server deleted successfully!' });
        setTimeout(() => setWebhookServerMessage(null), 5000);
        // Refresh the webhook servers
        await fetchWebhookServers();
      } else {
        setWebhookServerMessage({ type: 'danger', text: response.data?.message || 'Failed to delete webhook server.' });
        setTimeout(() => setWebhookServerMessage(null), 5000);
      }
    } catch (err) {
      setWebhookServerMessage({ type: 'danger', text: err.message || 'An error occurred while deleting webhook server.' });
      setTimeout(() => setWebhookServerMessage(null), 5000);
    }
  };

  const handleWebhookFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setWebhookFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const triggerEventDiscovery = async () => {
    try {
      setLoading(true);
      setError(null);
      setMessage(null);
      const response = await adminApi.post('/admin/callback/system/core/events/');
      if (response.data && response.data.success) {
        setMessage({ type: 'success', text: 'Event discovery triggered successfully! Refreshing data...' });
        fetchEventsWithConfigurations();
      } else {
        setMessage({ type: 'danger', text: response.data?.message || 'Failed to trigger event discovery.' });
      }
    } catch (err) {
      setMessage({ type: 'danger', text: err.message || 'An error occurred while triggering event discovery.' });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSidePanel = () => {
    setSidePanel({
      isOpen: false,
      type: null,
      eventData: null,
      configId: null
    });
  };

  const handleSaveSuccess = (successMessage = null) => {
    if (successMessage) {
      setMessage({ type: 'success', text: successMessage });
      // Clear the message after 5 seconds
      setTimeout(() => setMessage(null), 5000);
    }
    fetchEventsWithConfigurations();
    handleCloseSidePanel();
  };

  // Delete modal handlers
  const handleConfirmDelete = async () => {
    try {
      const response = await adminApi.delete(`/admin/callback/system/core/manage/configurations/${deleteModal.configId}/`);
      if (response.data && response.data.success) {
        setMessage({ type: 'success', text: `Configuration ${deleteModal.configId} deleted successfully!` });
        setTimeout(() => setMessage(null), 5000);
        fetchEventsWithConfigurations(); // Refresh the data
      } else {
        setMessage({ type: 'danger', text: response.data?.message || 'Failed to delete configuration.' });
        setTimeout(() => setMessage(null), 5000);
      }
    } catch (err) {
      setMessage({ type: 'danger', text: err.message || 'An error occurred while deleting configuration.' });
      setTimeout(() => setMessage(null), 5000);
    }
    setDeleteModal({ show: false, configId: null, configName: null });
  };

  const handleCancelDelete = () => {
    setDeleteModal({ show: false, configId: null, configName: null });
  };

  // Test results modal handlers
  const handleCloseTestResults = () => {
    setTestResultsModal(prev => ({
      ...prev,
      show: false
    }));
  };

  // Copy function using the utility
  const handleCopy = (text, itemKey) => {
    copyToClipboard(text, setCopyIconStates, itemKey);
  };

  // Test configuration handler
  const handleTestConfig = async (configId) => {
    try {
      // Show loading state in modal
      setTestResultsModal({
        show: true,
        configId: configId,
        results: [],
        loading: true
      });
      
      setMessage({ type: 'info', text: `Testing configuration ${configId}...` });
      const response = await adminApi.post(`/admin/callback/system/core/manage/configurations/${configId}/test/`);
      
      // Process response based on actual API structure
      let testResults = [];
      let testSummary = {};
      
      if (response.data && response.data.test_details) {
        const testDetails = response.data.test_details;
        testSummary = {
          callbackId: testDetails.callback_id,
          eventName: testDetails.event_name,
          totalServers: testDetails.total_servers || 0,
          successfulServers: testDetails.successful_servers || 0,
          failedServers: testDetails.failed_servers || 0,
          avgResponseTime: testDetails.avg_response_time_ms || 0,
          payloadPreview: testDetails.payload_preview
        };
        
        // Process server results
        if (testDetails.server_results && Array.isArray(testDetails.server_results)) {
          testResults = testDetails.server_results.map((result, index) => ({
            id: index,
            webhookUrl: result.webhook_url || 'Unknown server',
            description: result.description || 'No description',
            status: result.status_code || 'N/A',
            responseTime: result.response_time_ms || 0,
            success: result.success || false,
            message: result.message || 'No message',
            hmacEnabled: result.hmac_enabled || false,
            callbackId: result.callback_id || null,
            logId: result.log_id || null,
            errorMessage: result.error_message || null,
            timestamp: new Date().toISOString()
          }));
        }
        
        // Update modal with results
        setTestResultsModal({
          show: true,
          configId: configId,
          results: testResults,
          summary: testSummary,
          loading: false
        });
        
        // Show summary message based on API response
        if (response.data.success) {
          setMessage({
            type: 'success',
            text: response.data.message || `✅ Test completed successfully for all ${testSummary.totalServers} webhook server${testSummary.totalServers !== 1 ? 's' : ''}.`
          });
        } else {
          setMessage({
            type: 'warning',
            text: response.data.message || `⚠️ Test completed with some failures: ${testSummary.successfulServers}/${testSummary.totalServers} servers succeeded.`
          });
        }
      } else {
        setMessage({
          type: 'danger',
          text: `❌ Test failed: Invalid response format received.`
        });
        setTestResultsModal(prev => ({ ...prev, loading: false }));
      }
      
      setTimeout(() => setMessage(null), 10000);
    } catch (err) {
      console.error('Test configuration error:', err);
      
      // Try to extract error details
      let errorResults = [];
      let errorSummary = {};
      
      if (err.response?.data?.test_details?.server_results) {
        const testDetails = err.response.data.test_details;
        errorSummary = {
          callbackId: testDetails.callback_id || 'N/A',
          eventName: testDetails.event_name || 'N/A',
          totalServers: testDetails.total_servers || 0,
          successfulServers: testDetails.successful_servers || 0,
          failedServers: testDetails.failed_servers || 0,
          avgResponseTime: testDetails.avg_response_time_ms || 0
        };
        
        errorResults = testDetails.server_results.map((result, index) => ({
          id: index,
          webhookUrl: result.webhook_url || 'Unknown server',
          description: result.description || 'No description',
          status: result.status_code || 'Error',
          responseTime: result.response_time_ms || 0,
          success: false,
          message: result.message || result.error_message || 'Unknown error',
          hmacEnabled: result.hmac_enabled || false,
          callbackId: result.callback_id || testDetails.callback_id || null,
          logId: result.log_id || null,
          errorMessage: result.error_message || 'Request failed',
          timestamp: new Date().toISOString()
        }));
      } else {
        errorSummary = {
          callbackId: 'N/A',
          eventName: 'N/A',
          totalServers: 1,
          successfulServers: 0,
          failedServers: 1,
          avgResponseTime: 0
        };
        
        errorResults = [{
          id: 0,
          webhookUrl: 'Unknown server',
          description: 'Error occurred',
          status: 'Error',
          responseTime: 0,
          success: false,
          message: err.response?.data?.message || err.message || 'Unknown error',
          hmacEnabled: false,
          callbackId: null,
          logId: null,
          errorMessage: err.response?.data?.message || err.message || 'Request failed',
          timestamp: new Date().toISOString()
        }];
      }
      
      setTestResultsModal({
        show: true,
        configId: configId,
        results: errorResults,
        summary: errorSummary,
        loading: false
      });
      
      setMessage({
        type: 'danger',
        text: `❌ Test failed for configuration ${configId}. Click to view details.`
      });
      setTimeout(() => setMessage(null), 10000);
    }
  };

  const handleTimePeriodChange = (hours) => {
    setSelectedTimePeriod(hours);
    fetchCallbackMetrics(hours);
  };

  useEffect(() => {
    fetchWebhookServers();
    fetchEventsWithConfigurations();
    fetchCallbackMetrics(selectedTimePeriod);
  }, [fetchWebhookServers, fetchEventsWithConfigurations, fetchCallbackMetrics]);

  // Metrics display
  const renderMetrics = () => {
    if (metricsLoading && !metricsError && metrics.total_callbacks === 0) {
      return (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card">
              <div className="card-body text-center">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading metrics...</span>
                </div>
                <p className="mt-2">Loading callback metrics...</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const displayValue = (value, fallback = 'n/a') => {
      if (metricsError) return fallback;
      return typeof value === 'number' ? value.toLocaleString() : fallback;
    };

    const displayPercentage = (value, fallback = 'n/a') => {
      if (metricsError) return fallback;
      return typeof value === 'number' ? `${value}%` : fallback;
    };

    return (
      <div className="row">
        {/* Time Period Selector */}
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0">
                  Callback Metrics
                  {metricsError && (
                    <small className="text-muted ms-2">
                      (Error loading data -
                      <button
                        className="btn text-primary btn-sm"
                        onClick={() => fetchCallbackMetrics(selectedTimePeriod)}
                      >
                        Retry
                      </button>)
                    </small>
                  )}
                </h5>
                <div className="btn-group" role="group">
                  {[
                    { hours: 1, label: '1H' },
                    { hours: 6, label: '6H' },
                    { hours: 24, label: '24H' },
                    { hours: 168, label: '1W' }
                  ].map(({ hours, label }) => (
                    <button
                      key={hours}
                      type="button"
                      className={`btn btn-sm ${selectedTimePeriod === hours ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => handleTimePeriodChange(hours)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="row mt-4">
                <div className="card col-sm-3 border-0 mb-2">
                  <div className="card-body" style={{ borderLeft: '0.7rem solid var(--bs-primary)' }}>
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1">
                        <h5 className="text-muted fw-medium">Total Callbacks</h5>
                        <h4 className="mb-0">{displayValue(metrics.total_callbacks)}</h4>
                      </div>
                      <div className="avatar-sm rounded-circle bg-primary align-self-center">
                        <span className="avatar-title rounded-circle bg-primary">
                          <i className="fas fa-exchange-alt text-white font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card col-sm-3 border-0 mb-2">
                  <div className="card-body" style={{ borderLeft: '0.7rem solid var(--bs-success)' }}>
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1">
                        <h5 className="text-muted fw-medium">Successful</h5>
                        <h4 className="mb-0">{displayValue(metrics.successful_callbacks)}</h4>
                      </div>
                      <div className="avatar-sm rounded-circle bg-success align-self-center">
                        <span className="avatar-title rounded-circle bg-success">
                          <i className="fas fa-check-circle text-white font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card col-sm-3 border-0 mb-2">
                  <div className="card-body" style={{ borderLeft: '0.7rem solid var(--bs-danger)' }}>
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1">
                        <h5 className="text-muted fw-medium">Failed</h5>
                        <h4 className="mb-0">{displayValue(metrics.failed_callbacks)}</h4>
                      </div>
                      <div className="avatar-sm rounded-circle bg-danger align-self-center">
                        <span className="avatar-title rounded-circle bg-danger">
                          <i className="fas fa-times-circle text-white font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card col-sm-3 border-0 mb-2">
                  <div className="card-body" style={{ borderLeft: '0.7rem solid var(--bs-info)' }}>
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1">
                        <h5 className="text-muted fw-medium">Success Rate</h5>
                        <h4 className="mb-0">{displayPercentage(metrics.success_rate)}</h4>
                      </div>
                      <div className="avatar-sm rounded-circle bg-info align-self-center">
                        <span className="avatar-title rounded-circle bg-info">
                          <i className="fas fa-percent text-white font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Master grid column definitions (for events)
  const masterColumnDefs = [
    {
      headerName: 'Event Name',
      field: 'event_name',
      cellRenderer: 'agGroupCellRenderer', // Required for expand/collapse icons
      flex: 2,
      width: 250
    },
    {
      headerName: 'Configurations',
      field: 'config_count',
      width: 100,
      cellRenderer: (params) => {
        const count = params.value || 0;
        return (
          <span className={`badge ${count > 0 ? 'bg-primary' : 'bg-secondary'}`}>
            {count} config{count !== 1 ? 's' : ''}
          </span>
        );
      }
    },
    {
      headerName: 'Active',
      field: 'is_active',
      width: 50,
      cellRenderer: (params) => {
        if (typeof params.value === 'boolean') {
          return (
            <span className={`badge ${params.value ? 'bg-success' : 'bg-secondary'}`}>
              {params.value ? 'Active' : 'Inactive'}
            </span>
          );
        }
        return '—';
      }
    },
    {
      headerName: 'Actions',
      field: 'actions',
      pinned: 'right',
      cellRenderer: (params) => {
        const eventData = params.data?.eventData;
        const hasConfigurations = params.data?.config_count > 0;
        
        if (eventData) {
          return (
            <div className="btn" role="group">
              <button
                className="btn btn-sm btn-info me-2"
                onClick={() => setSidePanel({
                  isOpen: true,
                  type: 'viewEvent',
                  eventData: eventData,
                  configId: null
                })}
                title="View/Edit Event"
              >View Event</button>
              {hasConfigurations && (
                <button
                  className="btn btn-sm btn-secondary me-2"
                  onClick={() => {
                    // Expand the master-detail row to show configurations
                    const node = params.node;
                    if (node) {
                      node.setExpanded(!node.expanded);
                    }
                  }}
                  title="Show/Hide Configurations"
                >Edit Configurations
                </button>
              )}
              <button
                className="btn  btn-sm btn-primary"
                onClick={() => setSidePanel({
                  isOpen: true,
                  type: 'newConfig',
                  eventData: eventData,
                  configId: null
                })}
                title="Create Configuration"
              >Create Configuration
              </button>
            </div>
          );
        }
        return null;
      },
      sortable: false,
      filter: false
    }
  ];

  // Detail grid column definitions (for configurations)
  const detailColumnDefs = [
    {
      headerName: 'Config ID',
      field: 'config_id',
    },
    {
      headerName: 'Version',
      field: 'version',
      cellRenderer: (params) => {
        if (params.value) {
          return (
            <span className={`badge ${params.data.is_active ? 'bg-success' : 'bg-secondary'}`}>
              v{params.value}
            </span>
          );
        }
        return '—';
      }
    },
    {
      headerName: 'Priority',
      field: 'priority',
      cellRenderer: (params) => params.value || 'N/A'
    },
    {
      headerName: 'Message Template',
      field: 'message_template',
      cellRenderer: (params) => {
        const template = params.value;
        if (template && template.trim() !== '') {
          // Truncate long templates and show tooltip
          const maxLength = 200;
          const truncated = template.length > maxLength ?
            template.substring(0, maxLength) + '...' : template;
          return `${truncated}`;
        }
        return '—';
      }
    },
    {
      headerName: 'Health System',
      field: 'health_system_name',
    },
    {
      headerName: 'Provider',
      field: 'provider_name',
    },
    {
      headerName: 'Status',
      field: 'is_active',
      cellRenderer: (params) => {
        return (
          <span className={`badge ${params.value ? 'bg-success' : 'bg-secondary'}`}>
            {params.value ? 'Active' : 'Inactive'}
          </span>
        );
      }
    },
    {
      headerName: 'Created',
      field: 'created_at',
      cellRenderer: (params) => {
        if (params.value) {
          return new Date(params.value).toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          });
        }
        return '—';
      }
    },
    {
      headerName: 'Actions',
      field: 'actions',
      pinned: 'right',
      cellRenderer: (params) => {
        const handleDeleteConfig = (configId) => {
          // Find the configuration name for better UX
          const configName = `Configuration ${configId}`;
          setDeleteModal({
            show: true,
            configId: configId,
            configName: configName
          });
        };

        return (
          <div className="btn btn-sm" role="group">
            <button
              className="btn btn-info btn-sm me-2"
              onClick={() => setSidePanel({
                isOpen: true,
                type: 'viewConfig',
                eventData: null,
                configId: params.data.config_id
              })}
              title="View Configuration"
            ><i className="fas fa-eye me-1"></i> View </button>
            <button
              className="btn btn-primary btn-sm me-2"
              onClick={() => setSidePanel({
                isOpen: true,
                type: 'editConfig',
                eventData: null,
                configId: params.data.config_id
              })}
              title="Edit Configuration"
            >
              <i className="fas fa-edit me-1"></i>
              Edit
            </button>
            <button
              className="btn btn-success btn-sm me-2"
              onClick={() => handleTestConfig(params.data.config_id)}
              title="Test Configuration"
            >
              <i className="fas fa-play me-1"></i>
              Test
            </button>
            {testResultsModal.results.length > 0 && (
              <button
                className="btn btn-outline-info btn-sm me-2"
                onClick={() => setTestResultsModal(prev => ({ ...prev, show: true }))}
                title="Show Test Results"
              >
                <i className="fas fa-chart-line me-1"></i>
                Test Results
              </button>
            )}
            <button
              className="btn btn-danger btn-sm"
              onClick={() => handleDeleteConfig(params.data.config_id)}
              title="Delete Configuration"
            >
              <i className="fas fa-trash me-1"></i>
              Delete
            </button>
          </div>
        );
      },
      sortable: false,
      filter: false
    }
  ];

  return (
    <>
      {/* Page Title */}
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0 font-size-18">Callback Management</h4>
            <div className="page-title-right">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item"><span>Admin</span></li>
                <li className="breadcrumb-item active">Callbacks</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      {/* Metrics Section */}
      {renderMetrics()}

      {/* Core Server Settings Section */}
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="card-title mb-0">Webhook Server Management</h4>
          <button className="btn btn-primary btn-sm" onClick={handleCreateWebhook}
            disabled={webhookServersLoading} >
            <i className="fas fa-plus me-1"></i> Add Webhook Server
          </button>
        </div>
        <div className="card-body">
          {webhookServersLoading ? (
            <div className="text-center">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading webhook servers...</span>
              </div>
            </div>
          ) : (
            <>
              {webhookServersError && (
                <div className="alert alert-danger">{webhookServersError}</div>
              )}
              {webhookServerMessage && (
                <div className={`alert alert-${webhookServerMessage.type}`} role="alert">
                  {webhookServerMessage.text}
                </div>
              )}
              {/* Create/Edit Form */}
              {(isCreatingWebhook || editingWebhookId) && (
                <div className="card mb-3">
                  <div className="card-header">
                    <h5 className="mb-0">
                      {isCreatingWebhook ? 'Create Webhook Server' : 'Edit Webhook Server'}
                    </h5>
                  </div>
                  <div className="card-body">
                    <form onSubmit={(e) => { e.preventDefault(); handleSaveWebhook(); }}>
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label htmlFor="webhook-url" className="form-label">
                              <strong>Webhook URL *</strong>
                            </label>
                            <input
                              type="url"
                              className="form-control"
                              id="webhook-url"
                              name="core_server_webhook_url"
                              value={webhookFormData.core_server_webhook_url}
                              onChange={handleWebhookFormChange}
                              placeholder="https://your-server.com/webhook"
                              required
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label htmlFor="webhook-secret" className="form-label">
                              <strong>Webhook Secret</strong>
                            </label>
                            <input
                              type="password"
                              className="form-control"
                              id="webhook-secret"
                              name="core_server_webhook_secret"
                              value={webhookFormData.core_server_webhook_secret}
                              onChange={handleWebhookFormChange}
                              placeholder={editingWebhookId ? "Leave empty to keep current" : "Enter webhook secret"}
                            />
                            {editingWebhookId && (
                              <div className="form-text">
                                Leave empty to keep the current secret unchanged.
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="mb-3">
                        <label htmlFor="webhook-description" className="form-label">
                          <strong>Description</strong>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="webhook-description"
                          name="description"
                          value={webhookFormData.description}
                          onChange={handleWebhookFormChange}
                          placeholder="Optional description for this webhook server"
                        />
                      </div>
                      <div className="mb-3">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="webhook-active"
                            name="is_active"
                            checked={webhookFormData.is_active}
                            onChange={handleWebhookFormChange}
                          />
                          <label className="form-check-label" htmlFor="webhook-active">
                            Active
                          </label>
                        </div>
                      </div>
                      <div className="d-flex gap-2">
                        <button type="submit" className="btn btn-primary" disabled={webhookSaving}>
                          {webhookSaving ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                              {isCreatingWebhook ? 'Creating...' : 'Updating...'}
                            </>
                          ) : (
                            <>{isCreatingWebhook ? 'Create Webhook Server' : 'Update Webhook Server'}</>
                          )}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleCancelWebhookEdit}
                          disabled={webhookSaving}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* Webhook Servers Table */}
              {webhookServers.length === 0 ? (
                <div className="alert text-secondary text-center">
                  No webhook servers configured. Click &quot;Add Webhook Server&quot; to create one.
                </div>
              ) : (
                <AgGridTable
                  rowData={webhookServers}
                  height="100%"
                  columnDefs={[
                    {
                      headerName: 'URL',
                      field: 'core_server_webhook_url',
                      flex: 2,
                      cellRenderer: (params) => params.data ? <code className="text-break">{params.data.core_server_webhook_url}</code> : null
                    },
                    {
                      headerName: 'Secret',
                      field: 'core_server_webhook_secret',
                      width: 120,
                      valueGetter: (p) => p.data?.core_server_webhook_secret ? 'Configured' : 'Not Set',
                      cellRenderer: (params) => {
                        if (!params.data) return null;
                        return params.data.core_server_webhook_secret ? (
                          <span className="badge bg-success">Configured</span>
                        ) : (
                          <span className="badge bg-warning">Not Set</span>
                        );
                      }
                    },
                    {
                      headerName: 'Description',
                      field: 'description',
                      flex: 1,
                      valueGetter: (p) => p.data?.description || 'No description',
                      cellRenderer: (params) => {
                        if (!params.data) return null;
                        return params.data.description || <em className="text-muted">No description</em>;
                      }
                    },
                    {
                      headerName: 'Status',
                      field: 'is_active',
                      width: 100,
                      valueGetter: (p) => p.data?.is_active !== false ? 'Active' : 'Inactive',
                      cellRenderer: (params) => {
                        if (!params.data) return null;
                        return params.data.is_active !== false ? (
                          <span className="badge bg-success">Active</span>
                        ) : (
                          <span className="badge bg-secondary">Inactive</span>
                        );
                      }
                    },
                    {
                      headerName: 'Source',
                      field: 'source',
                      width: 120,
                      valueGetter: (p) => p.data?.source === 'settings_py' ? 'settings.py' : 'Database',
                      cellRenderer: (params) => {
                        if (!params.data) return null;
                        return params.data.source === 'settings_py' ? (
                          <span className="badge bg-info">settings.py</span>
                        ) : (
                          <span className="badge bg-primary">Database</span>
                        );
                      }
                    },
                    {
                      headerName: 'Actions',
                      field: 'actions',
                      width: 120,
                      sortable: false,
                      filter: false,
                      cellRenderer: (params) => {
                        if (!params.data) return null;
                        const webhook = params.data;
                        return (
                          <div className="btn-group btn-group-sm" role="group">
                            <button
                              className="btn btn-outline-primary"
                              onClick={() => handleEditWebhook(webhook)}
                              disabled={webhookSaving}
                              title="Edit webhook server"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            {webhook.source !== 'settings_py' && (
                              <button
                                className="btn btn-outline-danger"
                                onClick={() => handleDeleteWebhook(webhook.id)}
                                disabled={webhookSaving}
                                title="Delete webhook server"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            )}
                          </div>
                        );
                      }
                    }
                  ]}
                  defaultColDef={{
                    minWidth: 80,
                    resizable: true,
                    sortable: true,
                    filter: true,
                  }}
                  gridOptions={{
                    domLayout: 'autoHeight',
                    autoSizeStrategy: { type: 'fitGridWidth' }
                  }}
                  getRowId={(params) => `${params.data.id}`}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Unified Events and Configurations Table */}
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="card-title mb-0">Callback Events & Configurations</h4>
          <button onClick={triggerEventDiscovery} className="btn text-primary" disabled={loading}>
            <i className="fa fa-refresh"></i> {loading ? 'Discovering...' : 'Re-Discover Events'}
          </button>
        </div>
        <div style={{ maxHeight: '800px', overflowY: 'auto' }}>
          {message && (
            <div
              className={`alert alert-${message.type} ${testResultsModal.results.length > 0 ? 'alert-dismissible' : ''}`}
              role="alert"
              style={{ cursor: testResultsModal.results.length > 0 ? 'pointer' : 'default' }}
              onClick={() => {
                if (testResultsModal.results.length > 0) {
                  setTestResultsModal(prev => ({ ...prev, show: true }));
                }
              }}
            >
              {message.text}
              {testResultsModal.results.length > 0 && (
                <small className="d-block mt-1">
                  <i className="fas fa-info-circle me-1"></i>
                  Click to view detailed results
                </small>
              )}
            </div>
          )}
          {error && (
            <div className="alert alert-danger" role="alert">
              Error: {error}
            </div>
          )}
          {loading ? (
            <div className="text-center">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading events and configurations...</span>
              </div>
            </div>
          ) : (
            <div>
              <AgGridTable
                rowData={eventsWithConfigs}
                columnDefs={masterColumnDefs}
                domLayout='normal'
                defaultColDef={{
                  resizable: true,
                  sortable: true,
                  filter: true,
                }}
                autoSizeStrategy={{
                  type: 'fitGridWidth'
                }}
                rowSelection={false}
                masterDetail={true}
                isRowMaster={() => true} // Make all rows expandable
                detailCellRendererParams={{
                  detailGridOptions: {
                    columnDefs: detailColumnDefs,
                    defaultColDef: {
                      resizable: true,
                      sortable: true,
                      filter: true,
                    },
                    autoSizeStrategy: {
                      type: 'fitCellContents',
                      skipHeader: false,
                    },
                    domLayout: 'autoHeight',
                    overlayNoRowsTemplate: '<div class="text-center text-secondary">No configurations found for this event</div>'
                  },
                  getDetailRowData: (params) => {
                    const configurations = params.data.configurations || [];
                    params.successCallback(configurations);
                  }
                }}
                // Provide a unique ID for each row
                getRowId={(params) => params.data.id}
                gridRef={mainGridRef}
              />
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog">
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button type="button" className="btn-close" onClick={handleCancelDelete}></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete <strong>{deleteModal.configName}</strong>?</p>
                <p className="text-muted">This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-danger" onClick={handleConfirmDelete}>
                  Delete
                </button>
                <button type="button" className="btn btn-secondary" onClick={handleCancelDelete}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {deleteModal.show && <div className="modal-backdrop fade show"></div>}

      {/* Test Results Modal */}
      {testResultsModal.show && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-xl" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Webhook Test Results - Configuration {testResultsModal.configId}
                </h5>
                <button type="button" className="btn-close" onClick={handleCloseTestResults}></button>
              </div>
              <div className="modal-body">
                {testResultsModal.loading ? (
                  <div className="text-center">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Testing webhooks...</span>
                    </div>
                    <p className="mt-2">Testing webhook servers...</p>
                  </div>
                ) : (
                  <>
                    {/* Summary Section */}
                    {testResultsModal.summary && (
                      <div className="card mb-4">
                        <div className="card-header">
                          <h6 className="mb-0">Test Summary</h6>
                        </div>
                        <div className="card-body">
                          <div className="row">
                            <div className="col-md-3">
                              <strong>Event:</strong> {testResultsModal.summary.eventName || 'N/A'}
                            </div>
                            {/* <div className="col-md-3">
                              <strong>Callback ID:</strong>
                              <small className="d-block text-muted">{testResultsModal.summary.callbackId || 'N/A'}</small>
                            </div> */}
                            <div className="col-md-3">
                              <strong>MiraSol Case ID:</strong> {testResultsModal.summary.payloadPreview?.mirasol_case_id || 'N/A'}
                            </div>
                            <div className="col-md-3">
                              <strong>Servers:</strong> {testResultsModal.summary.successfulServers || 0}/{testResultsModal.summary.totalServers || 0} successful
                            </div>
                            <div className="col-md-3">
                              <strong>Avg Response Time:</strong> {testResultsModal.summary.avgResponseTime || 0}ms
                            </div>
                          </div>
                          {testResultsModal.summary.payloadPreview && (
                            <div className="mt-3">
                              <strong>Payload Preview:</strong>
                              <pre className="bg-light p-2 mt-1" style={{ fontSize: '0.85em', maxHeight: '150px', overflow: 'auto' }}>
                                {JSON.stringify(testResultsModal.summary.payloadPreview, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Server Results */}
                    <div className="card">
                      <div className="card-header">
                        <h6 className="mb-0">Server Results ({testResultsModal.results.length})</h6>
                      </div>
                      <div className="card-body p-0">
                        {testResultsModal.results.length === 0 ? (
                          <div className="text-center p-4 text-muted">
                            No server results available
                          </div>
                        ) : (
                          <div className="table-responsive">
                            <table className="table table-striped mb-0">
                              <thead>
                                <tr>
                                  <th>Server</th>
                                  <th>Status</th>
                                  <th>Response Time</th>
                                  <th>Message</th>
                                  <th>Security</th>
                                  <th>Callback ID</th>
                                  <th>Log ID</th>
                                </tr>
                              </thead>
                              <tbody>
                                {testResultsModal.results.map((result) => (
                                  <tr key={result.id}>
                                    <td>
                                      <div>
                                        <strong className="text-break">{result.webhookUrl}</strong>
                                        {result.description && (
                                          <small className="d-block text-muted">{result.description}</small>
                                        )}
                                      </div>
                                    </td>
                                    <td>
                                      <span className={`badge ${result.success ? 'bg-success' : 'bg-danger'}`}>
                                        {result.status}
                                      </span>
                                    </td>
                                    <td>
                                      <span className={`badge ${
                                        result.responseTime < 500 ? 'bg-success' :
                                        result.responseTime < 1000 ? 'bg-warning' : 'bg-danger'
                                      }`}>
                                        {result.responseTime}ms
                                      </span>
                                    </td>
                                    <td>
                                      <div>
                                        {result.message}
                                        {result.errorMessage && (
                                          <small className="d-block text-danger mt-1">
                                            Error: {result.errorMessage}
                                          </small>
                                        )}
                                      </div>
                                    </td>
                                    <td>
                                      <span className={`badge ${result.hmacEnabled ? 'bg-success' : 'bg-secondary'}`}>
                                        {result.hmacEnabled ? 'HMAC Enabled' : 'No HMAC'}
                                      </span>
                                    </td>
                                    <td>
                                      {result.callbackId ? (
                                        <div className="d-flex align-items-center">
                                          <small className="text-muted font-monospace me-2">
                                            {result.callbackId.substring(0, 8)}...
                                          </small>
                                          <button
                                            className="btn text-primary p-1"
                                            onClick={() => handleCopy(result.callbackId, `callback-${result.id}`)}
                                            title="Copy Callback ID"
                                            style={{ fontSize: '0.7rem', lineHeight: 1 }}
                                          >
                                            <i className={`fas ${getCopyIcon(copyIconStates[`callback-${result.id}`])}`}></i>
                                          </button>
                                        </div>
                                      ) : (
                                        <span className="text-muted">—</span>
                                      )}
                                    </td>
                                    <td>
                                      {result.logId ? (
                                        <div className="d-flex align-items-center">
                                          <small className="text-muted font-monospace me-2">
                                            {result.logId.substring(0, 8)}...
                                          </small>
                                          <button
                                            className="btn text-primary p-1"
                                            onClick={() => handleCopy(result.logId, `log-${result.id}`)}
                                            title="Copy Log ID"
                                            style={{ fontSize: '0.7rem', lineHeight: 1 }}
                                          >
                                            <i className={`fas ${getCopyIcon(copyIconStates[`log-${result.id}`])}`}></i>
                                          </button>
                                        </div>
                                      ) : (
                                        <span className="text-muted">—</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseTestResults}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {testResultsModal.show && <div className="modal-backdrop fade show"></div>}

      {/* Side Panel */}
      <SidebarPanel
        isOpen={sidePanel.isOpen}
        onClose={handleCloseSidePanel}
        title={
          sidePanel.type === 'viewEvent' ? 'View/Edit Event' :
          sidePanel.type === 'viewConfig' ? 'View Event Configuration' :
          sidePanel.type === 'editConfig' ? 'Edit Event Configuration' :
          sidePanel.type === 'newConfig' ? 'Create Event Configuration' : ''
        }
      >
        {sidePanel.type === 'viewEvent' && sidePanel.eventData && (
          <CallbackEventForm
            eventData={sidePanel.eventData}
            mode="edit"
            onClose={handleCloseSidePanel}
            onSaveSuccess={handleSaveSuccess}
          />
        )}
        {sidePanel.type === 'viewConfig' && sidePanel.configId && (
          <CallbackConfigurationForm
            configId={sidePanel.configId}
            mode="view"
            coreServerWebhookUrl={webhookServers.length > 0 ? webhookServers[0].core_server_webhook_url : null}
            onClose={handleCloseSidePanel}
            onSaveSuccess={handleSaveSuccess}
          />
        )}
        {sidePanel.type === 'editConfig' && sidePanel.configId && (
          <CallbackConfigurationForm
            configId={sidePanel.configId}
            mode="edit"
            coreServerWebhookUrl={webhookServers.length > 0 ? webhookServers[0].core_server_webhook_url : null}
            onClose={handleCloseSidePanel}
            onSaveSuccess={handleSaveSuccess}
          />
        )}
        {sidePanel.type === 'newConfig' && sidePanel.eventData && (
          <CallbackConfigurationForm
            mode="new"
            eventName={sidePanel.eventData.event_name}
            eventId={sidePanel.eventData.id}
            coreServerWebhookUrl={webhookServers.length > 0 ? webhookServers[0].core_server_webhook_url : null}
            onClose={handleCloseSidePanel}
            onSaveSuccess={handleSaveSuccess}
          />
        )}
      </SidebarPanel>
    </>
  );
};

export default CallbackManagementPage;
